import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Upload, FileSpreadsheet, Plus, Search, AlertCircle, CheckCircle2, Loader2, FileX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { listAccountImports } from "@/lib/contacts.functions";
import { listMyWorkspaces } from "@/lib/workspaces.functions";
import { formatDistanceToNow } from "date-fns";
import { EmptyState } from "@/components/empty-state";

export const Route = createFileRoute("/_authenticated/imports")({
  component: ImportsPage,
});

const STATUS_META: Record<string, { label: string; tone: "default" | "secondary" | "destructive" | "outline" }> = {
  processed: { label: "Processed", tone: "default" },
  processing: { label: "Processing", tone: "secondary" },
  failed: { label: "Failed", tone: "destructive" },
  needs_mapping: { label: "Needs mapping", tone: "outline" },
  skipped_duplicate: { label: "Duplicate", tone: "outline" },
};

function formatBytes(b: number | null | undefined) {
  if (!b) return ",";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function ImportsPage() {
  const listImports = useServerFn(listAccountImports);
  const listWorkspaces = useServerFn(listMyWorkspaces);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const wsQ = useQuery({ queryKey: ["my-workspaces"], queryFn: () => listWorkspaces() });
  const impQ = useQuery({
    queryKey: ["account-imports", search, statusFilter],
    queryFn: () =>
      listImports({
        data: {
          search: search || null,
          status: (statusFilter as "processing" | "processed" | "failed" | "needs_mapping" | "skipped_duplicate" | null) || null,
          limit: 100,
        },
      }),
  });

  const workspaces = wsQ.data?.workspaces ?? [];
  const imports = impQ.data?.imports ?? [];
  const hasWorkspaces = workspaces.length > 0;
  const hasImports = imports.length > 0;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Imports</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every CSV uploaded across your workspaces, newest first.
          </p>
        </div>
        {hasWorkspaces && (
          <Button asChild>
            <Link to="/workspaces">
              <Upload className="mr-2 h-4 w-4" />
              Upload a CSV
            </Link>
          </Button>
        )}
      </div>

      {!hasWorkspaces && !wsQ.isLoading ? (
        <EmptyState
          icon={FileSpreadsheet}
          title="Create a workspace first"
          description="Imports live inside workspaces. Spin one up for each affiliate program, then upload your first CSV."
          action={
            <Button asChild>
              <Link to="/workspaces">
                <Plus className="mr-2 h-4 w-4" />
                New workspace
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative max-w-xs flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by filename…"
                className="pl-8"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {(["processed", "processing", "failed", "needs_mapping"] as const).map((s) => (
                <Button
                  key={s}
                  variant={statusFilter === s ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(statusFilter === s ? null : s)}
                >
                  {STATUS_META[s]?.label ?? s}
                </Button>
              ))}
              {statusFilter && (
                <Button variant="ghost" size="sm" onClick={() => setStatusFilter(null)}>
                  Clear
                </Button>
              )}
            </div>
          </div>

          {impQ.isLoading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading imports…
            </div>
          ) : !hasImports ? (
            <EmptyState
              icon={Upload}
              variant={search || statusFilter ? "filtered" : "first-run"}
              title={search || statusFilter ? "No imports match those filters" : "Your imports appear here"}
              description={
                search || statusFilter
                  ? "Try clearing the filter or searching a different filename."
                  : "Open a workspace and drop a CSV in to get started. We'll auto-detect columns, deduplicate, and surface anomalies."
              }
              action={
                search || statusFilter ? (
                  <Button variant="ghost" onClick={() => { setSearch(""); setStatusFilter(null); }}>Clear filters</Button>
                ) : (
                  <Button asChild>
                    <Link to="/workspaces">Go to workspaces</Link>
                  </Button>
                )
              }
            />
          ) : (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2.5">File</th>
                    <th className="px-4 py-2.5">Workspace</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5 text-right">Rows</th>
                    <th className="px-4 py-2.5 text-right">Size</th>
                    <th className="px-4 py-2.5">Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {imports.map((imp) => {
                    const meta = STATUS_META[imp.status as string] ?? { label: imp.status as string, tone: "outline" as const };
                    const ws = (imp as { workspaces?: { program_name?: string } | null }).workspaces;
                    return (
                      <tr key={imp.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {imp.status === "failed" ? (
                              <FileX className="h-4 w-4 text-destructive" />
                            ) : imp.status === "processed" ? (
                              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                            ) : (
                              <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="font-medium">{imp.source_filename ?? "Untitled"}</span>
                          </div>
                          {imp.error_message && (
                            <div className="mt-1 flex items-start gap-1 text-xs text-destructive">
                              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
                              <span className="line-clamp-2">{imp.error_message}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to="/workspaces/$workspaceId"
                            params={{ workspaceId: imp.workspace_id as string }}
                            className="text-primary hover:underline"
                          >
                            {ws?.program_name ?? "Workspace"}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={meta.tone}>{meta.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {imp.row_count ?? ","}
                          {imp.rows_inserted != null && imp.rows_inserted > 0 && (
                            <span className="ml-1 text-xs text-emerald-500">+{imp.rows_inserted}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                          {formatBytes(imp.file_size_bytes)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {imp.uploaded_at
                            ? formatDistanceToNow(new Date(imp.uploaded_at), { addSuffix: true })
                            : ","}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

